import type { StripePlan } from "@gosaas/types/stripe";
import { Loader2 } from "lucide-react";
import { ApiQueryAddonSlider } from "@/components/billing/api-query-addon-slider";
import { Button } from "@/components/ui/button";
import type { ApiQueryAddonTier } from "@/utils/api-pricing";
import { effectiveApiQueries, formatQueryCount } from "@/utils/api-pricing";
import {
	addonAmountCents,
	billingIntervalLabel,
	checkoutTotalCents,
	formatMoney,
	planBillingAmountCents,
} from "@/utils/plan-checkout";

type PlanCheckoutConfiguratorProps = {
	plan: StripePlan;
	isAnnual: boolean;
	addonTiers: ApiQueryAddonTier[];
	addonIndex: number;
	onAddonIndexChange: (index: number) => void;
	onCheckout?: () => void;
	checkoutLabel?: string;
	checkoutDisabled?: boolean;
	isCheckoutLoading?: boolean;
	showCheckoutButton?: boolean;
	sliderDisabled?: boolean;
	mode?: "checkout" | "subscription";
	footer?: React.ReactNode;
};

export function PlanCheckoutConfigurator({
	plan,
	isAnnual,
	addonTiers,
	addonIndex,
	onAddonIndexChange,
	onCheckout,
	checkoutLabel = "Checkout",
	checkoutDisabled = false,
	isCheckoutLoading = false,
	showCheckoutButton = true,
	sliderDisabled = false,
	mode = "checkout",
	footer,
}: PlanCheckoutConfiguratorProps) {
	const planAmount = planBillingAmountCents(plan, isAnnual);
	const addonAmount = addonAmountCents(addonIndex, addonTiers, isAnnual);
	const total = checkoutTotalCents(plan, isAnnual, addonIndex, addonTiers);
	const interval = billingIntervalLabel(isAnnual);
	const intervalSuffix = isAnnual ? "/yr" : "/mo";

	return (
		<div
			className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)] lg:items-start"
			data-testid="plan-checkout-configurator"
		>
			<div className="rounded-2xl border border-border/60 bg-card/50 p-6 lg:p-8">
				<ApiQueryAddonSlider
					planName={plan.name}
					tiers={addonTiers}
					isAnnual={isAnnual}
					value={addonIndex}
					onValueChange={onAddonIndexChange}
					hideActions
					disabled={sliderDisabled}
				/>
			</div>

			<div className="rounded-2xl border border-border/60 bg-muted/20 p-6 lg:sticky lg:top-24">
				<h3 className="font-semibold text-lg tracking-tight">
					{mode === "subscription" ? "Subscription summary" : "Order summary"}
				</h3>
				<p className="mt-1 text-muted-foreground text-sm">
					{plan.name} plan · Billed {isAnnual ? "annually" : "monthly"}
				</p>

				<dl className="mt-6 space-y-3 text-sm">
					<div className="flex items-start justify-between gap-4">
						<dt className="text-muted-foreground">
							{plan.name} plan
							<span className="mt-0.5 block text-xs">
								{formatQueryCount(effectiveApiQueries(plan.name, 0))} API
								queries/mo included
							</span>
						</dt>
						<dd className="font-medium tabular-nums">
							{formatMoney(planAmount)}
							{intervalSuffix}
						</dd>
					</div>

					{addonAmount > 0 && (
						<div className="flex items-start justify-between gap-4">
							<dt className="text-muted-foreground">API query add-on</dt>
							<dd className="font-medium tabular-nums">
								{formatMoney(addonAmount)}
								{intervalSuffix}
							</dd>
						</div>
					)}

					<div className="border-border/60 border-t pt-3">
						<div className="flex items-baseline justify-between gap-4">
							<dt className="font-semibold">Total</dt>
							<dd className="font-bold text-2xl tabular-nums tracking-tight">
								{formatMoney(total)}
								<span className="font-normal text-base text-muted-foreground">
									/{interval}
								</span>
							</dd>
						</div>
						<p className="mt-2 text-muted-foreground text-xs">
							{mode === "subscription"
								? "Add-on changes update your subscription immediately."
								: "Taxes may apply. You can change add-ons anytime after subscribing."}
						</p>
					</div>
				</dl>

				{showCheckoutButton && onCheckout && (
					<Button
						type="button"
						className="mt-6 w-full py-6 text-base"
						disabled={checkoutDisabled || isCheckoutLoading}
						onClick={onCheckout}
						data-testid="plan-checkout-continue"
					>
						{isCheckoutLoading ? (
							<Loader2 className="size-5 animate-spin" />
						) : (
							checkoutLabel
						)}
					</Button>
				)}

				{footer}
			</div>
		</div>
	);
}
